import { useEffect, useRef, useState } from "react";

const AT_BOTTOM_THRESHOLD_PX = 48;
const TOP_OF_ELEMENT_OFFSET_PX = 24;

const isNearBottom = (element: HTMLElement) =>
  element.scrollHeight - element.scrollTop - element.clientHeight <= AT_BOTTOM_THRESHOLD_PX;

export const useStickToBottom = () => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const stuckRef = useRef(true);
  const [isAtBottom, setIsAtBottom] = useState(true);

  const scrollToBottom = (behavior: ScrollBehavior = "auto") => {
    const scroller = scrollRef.current;
    if (!scroller) return;
    stuckRef.current = true;
    setIsAtBottom(true);
    scroller.scrollTo({ top: scroller.scrollHeight, behavior });
  };

  const scrollToTopOf = (element: HTMLElement | null, behavior: ScrollBehavior = "auto") => {
    const scroller = scrollRef.current;
    if (!scroller || !element) return;
    stuckRef.current = false;
    const top =
      scroller.scrollTop +
      element.getBoundingClientRect().top -
      scroller.getBoundingClientRect().top -
      TOP_OF_ELEMENT_OFFSET_PX;
    scroller.scrollTo({ top, behavior });
    setIsAtBottom(isNearBottom(scroller));
  };

  const onScroll = () => {
    const scroller = scrollRef.current;
    if (!scroller) return;
    const nearBottom = isNearBottom(scroller);
    stuckRef.current = nearBottom;
    setIsAtBottom(nearBottom);
  };

  useEffect(() => {
    const scroller = scrollRef.current;
    const content = contentRef.current;
    if (!scroller || !content) return;

    scroller.scrollTop = scroller.scrollHeight;
    const observer = new ResizeObserver(() => {
      if (stuckRef.current) scroller.scrollTop = scroller.scrollHeight;
    });
    observer.observe(content);

    return () => observer.disconnect();
  }, []);

  return { scrollRef, contentRef, isAtBottom, onScroll, scrollToBottom, scrollToTopOf };
};
